import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../supabase/client';
import { MessageContent } from '../../types';

async function extractPdfText(file: File): Promise<string> {
  const pdfjsLib = await import('pdfjs-dist');
  const workerUrl = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).href;
  pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

  let text = '';
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    text += content.items
      .map((item: any) => ('str' in item ? item.str : ''))
      .join(' ') + '\n';
  }
  return text.trim();
}

// ─────────────────────────────────────────────────────────────────────────────
// Simple markdown renderer (handles ## headings, **bold**, *italic*)
// ─────────────────────────────────────────────────────────────────────────────

function renderInline(text: string, keyPrefix: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  const regex = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    const raw = match[0];
    if (raw.startsWith('**')) {
      parts.push(<strong key={`${keyPrefix}-${i++}`}>{raw.slice(2, -2)}</strong>);
    } else {
      parts.push(<em key={`${keyPrefix}-${i++}`}>{raw.slice(1, -1)}</em>);
    }
    last = match.index + raw.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function renderMarkdown(text: string): React.ReactNode {
  const lines = text.split('\n');
  return lines.map((line, idx) => {
    if (line.startsWith('## ')) {
      return (
        <h2 key={idx} className="text-sm font-semibold text-gray-900 mt-4 mb-1 first:mt-0 border-b border-gray-100 pb-1">
          {line.slice(3)}
        </h2>
      );
    }
    if (line.startsWith('### ')) {
      return (
        <h3 key={idx} className="text-sm font-semibold text-gray-800 mt-3 mb-0.5">
          {line.slice(4)}
        </h3>
      );
    }
    if (line.trim() === '---') {
      return <hr key={idx} className="my-3 border-gray-200" />;
    }
    if (line.trim() === '') {
      return <div key={idx} className="h-2" />;
    }
    return (
      <p key={idx} className="text-sm text-gray-800 leading-relaxed">
        {renderInline(line, `line-${idx}`)}
      </p>
    );
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Campaign parser — extract AI-suggested campaigns from raw response text
// ─────────────────────────────────────────────────────────────────────────────

interface ParsedCampaign {
  title: string;
  messages: { number: number; content: string }[];
}

function parseAICampaigns(rawText: string): ParsedCampaign[] {
  const results: ParsedCampaign[] = [];

  // Strip bold/italic markdown from campaign and message headers so regex matches cleanly
  const normalized = rawText
    .replace(/\*\*(Campaign [A-Z] —[^*\n]+)\*\*/g, '$1')
    .replace(/\*(Message \d+[^*:]+:)\*/g, '$1');

  // Find all campaign header positions
  const campaignHeaderRe = /^Campaign ([A-Z]) — (.+)$/gm;
  const campaignPositions: Array<{ index: number; title: string }> = [];
  let cm: RegExpExecArray | null;
  while ((cm = campaignHeaderRe.exec(normalized)) !== null) {
    campaignPositions.push({ index: cm.index, title: cm[2].trim() });
  }

  for (let ci = 0; ci < campaignPositions.length; ci++) {
    const blockStart = campaignPositions[ci].index;
    const blockEnd = ci + 1 < campaignPositions.length
      ? campaignPositions[ci + 1].index
      : normalized.length;
    const block = normalized.slice(blockStart, blockEnd);
    const title = campaignPositions[ci].title;

    // Find all message header positions within this campaign block
    const msgHeaderRe = /^Message (\d+)\s*\([^)]*\)\s*:/gm;
    const msgPositions: Array<{ index: number; num: number }> = [];
    let mm: RegExpExecArray | null;
    while ((mm = msgHeaderRe.exec(block)) !== null) {
      msgPositions.push({ index: mm.index, num: parseInt(mm[1]) });
    }

    const parsedMessages: { number: number; content: string }[] = [];
    for (let mi = 0; mi < msgPositions.length; mi++) {
      const contentStart = msgPositions[mi].index;
      const contentEnd = mi + 1 < msgPositions.length
        ? msgPositions[mi + 1].index
        : block.length;

      let content = block.slice(contentStart, contentEnd);
      // Remove the message header line itself
      content = content.replace(/^Message \d+\s*\([^)]*\)\s*:\s*\n/, '').trim();
      // Strip common closing signatures
      content = content
        .replace(/\n\nBest,[\s\S]*$/, '')
        .replace(/\n\nWarm regards,[\s\S]*$/, '')
        .replace(/\n\nSincerely,[\s\S]*$/, '')
        .replace(/\n\nThanks,[\s\S]*$/, '')
        .replace(/\n\nRegards,[\s\S]*$/, '')
        .trim();

      if (content) {
        parsedMessages.push({ number: msgPositions[mi].num, content });
      }
    }

    if (parsedMessages.length > 0) {
      results.push({ title, messages: parsedMessages });
    }
  }

  return results;
}

function toMessageContents(parsed: ParsedCampaign): MessageContent[] {
  return parsed.messages.map((msg, i) => ({
    message_number: msg.number,
    content: msg.content,
    character_count: msg.content.length,
    delay_days: i === 0 ? 0 : 3,
  }));
}

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface CampaignContext {
  campaign_name?: string;
  sequence_name?: string;
  company_name?: string;
  target_role?: string;
  company_type?: string;
  geography?: string;
  industry?: string;
  company_size?: string;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface GuidedAction {
  label: string;
  prompt: string;
  requiresInput?: boolean;
  inputPlaceholder?: string;
  inputTokenPlaceholder?: string;
}

type CampaignGoal = 'lead_generation' | 'product_discovery';

interface SavedSession {
  id: string;
  display_history: ChatMessage[];
  api_history: ChatMessage[];
  initial_analysis: string | null;
  turns_used: number;
  product_context: string | null;
  domain_keyword: string | null;
  website_url: string | null;
  context_document: string | null;
  campaign_goal: CampaignGoal | null;
  icp_description: string | null;
  created_at: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  messages: MessageContent[];
  campaignContext: CampaignContext;
  onLoadMessages?: (messages: MessageContent[]) => void;
  campaignId?: string;
  initialGoal?: CampaignGoal | null; // Epoch 80: pre-populate from wizard Step 1
}

type ModalPhase = 'loading_session' | 'resume_prompt' | 'context_panel' | 'analyzing';

const MAX_FOLLOW_UPS = 7;
const ANALYSIS_TIMEOUT_MS = 30000;

const GUIDED_ACTIONS: GuidedAction[] = [
  {
    label: '✍️ Write another campaign',
    prompt: 'Write a third complete 4-message campaign with a different angle from the two you already suggested. Make it meaningfully distinct — different hook style, different pain assumption, or different ICP framing.',
  },
  {
    label: '📏 Shorten Message 1',
    prompt: 'Rewrite Message 1 only. Keep the hook and qualifying question. Cut everything else. Target under 250 characters.',
  },
  {
    label: '🎯 Try 3 different hooks',
    prompt: 'Give me 3 alternative opening lines for Message 1 only. Each should use a different hook style: one quantified pain hook, one external pressure hook, one direct qualifying question. Just the opening lines — 1–2 sentences each.',
  },
  {
    label: '📋 Strengthen the follow-up',
    prompt: 'Rewrite the final follow-up message in the sequence incorporating a specific case study or VOC quote. Make it concrete: company type, outcome, timeframe. This should be the message that converts skeptical leads.',
  },
  {
    label: '🔢 What should I fix first?',
    prompt: 'Given everything you flagged, what is the single most important change I should make right now? Give me the exact replacement text, not just a direction.',
  },
  {
    label: '🔄 Adapt for a different role',
    prompt: 'Rewrite the entire message sequence targeting [ROLE] instead of the current target. Keep the same product. Adjust the pain assumption, qualifying question, and case reference to match the new title\'s mandate.',
    requiresInput: true,
    inputPlaceholder: 'Which role? (e.g. "Head of Content")',
    inputTokenPlaceholder: '[ROLE]',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const CampaignAnalyzerModal: React.FC<Props> = ({
  isOpen,
  onClose,
  messages,
  campaignContext,
  onLoadMessages,
  campaignId,
  initialGoal,
}) => {
  // Phase state machine
  const [phase, setPhase] = useState<ModalPhase>('loading_session');
  const [savedSession, setSavedSession] = useState<SavedSession | null>(null);
  const [guidedActions, setGuidedActions] = useState<GuidedAction[]>(GUIDED_ACTIONS);

  // Campaign goal (required)
  const [campaignGoal, setCampaignGoal] = useState<CampaignGoal | null>(null);

  // Product context fields (Epoch 79)
  const [productContext, setProductContext] = useState('');
  const [icpDescription, setIcpDescription] = useState('');
  const [domainKeyword, setDomainKeyword] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [websiteUrlError, setWebsiteUrlError] = useState('');
  const [contextDocument, setContextDocument] = useState('');
  const [docFileName, setDocFileName] = useState<string | null>(null);
  const [docLoading, setDocLoading] = useState(false);
  const [docError, setDocError] = useState<string | null>(null);

  // Analysis state
  const [isLoading, setIsLoading] = useState(false);
  const [initialAnalysis, setInitialAnalysis] = useState<string | null>(null);
  const [displayHistory, setDisplayHistory] = useState<ChatMessage[]>([]);
  const [apiHistory, setApiHistory] = useState<ChatMessage[]>([]);
  const [turnsUsed, setTurnsUsed] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [activeSubInput, setActiveSubInput] = useState<string | null>(null);
  const [subInputValue, setSubInputValue] = useState('');

  // Parsed campaigns for "Load into editor"
  const [parsedCampaigns, setParsedCampaigns] = useState<ParsedCampaign[]>([]);

  const chatBottomRef = useRef<HTMLDivElement>(null);
  const subInputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Refs hold context so callAnalyzer always reads current values
  const productContextRef = useRef('');
  const icpDescriptionRef = useRef('');
  const domainKeywordRef = useRef('');
  const websiteUrlRef = useRef('');
  const contextDocumentRef = useRef('');
  const campaignGoalRef = useRef<CampaignGoal | null>(null);

  // ── On modal open ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;

    // Reset analysis state
    setInitialAnalysis(null);
    setDisplayHistory([]);
    setApiHistory([]);
    setTurnsUsed(0);
    setInputValue('');
    setError(null);
    setActiveSubInput(null);
    setSubInputValue('');
    setParsedCampaigns([]);
    setSavedSession(null);

    // Load guided prompts from DB (fallback to constants if fetch fails)
    supabase
      .from('campaign_ai_guided_prompts')
      .select('label, prompt, requires_input, input_placeholder, input_token_placeholder')
      .eq('is_active', true)
      .order('sort_order')
      .then(({ data }) => {
        if (data && data.length > 0) {
          setGuidedActions(data.map(row => ({
            label: row.label,
            prompt: row.prompt,
            requiresInput: row.requires_input,
            inputPlaceholder: row.input_placeholder ?? undefined,
            inputTokenPlaceholder: row.input_token_placeholder ?? undefined,
          })));
        }
      });

    if (campaignId) {
      checkForSession();
    } else {
      setCampaignGoal(initialGoal ?? null);
      setProductContext('');
      setIcpDescription('');
      setDomainKeyword('');
      setWebsiteUrl('');
      setContextDocument('');
      setDocFileName(null);
      setDocLoading(false);
      setDocError(null);
      setPhase('context_panel');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const checkForSession = async () => {
    setPhase('loading_session');
    try {
      const { data } = await supabase
        .from('campaign_ai_sessions')
        .select('*')
        .eq('campaign_id', campaignId)
        .maybeSingle();

      if (data) {
        setSavedSession(data as SavedSession);
        setPhase('resume_prompt');
      } else {
        setCampaignGoal(initialGoal ?? null);
        setProductContext('');
        setIcpDescription('');
        setDomainKeyword('');
        setWebsiteUrl('');
        setContextDocument('');
        setDocFileName(null);
        setPhase('context_panel');
      }
    } catch {
      setCampaignGoal(initialGoal ?? null);
      setProductContext('');
      setDomainKeyword('');
      setWebsiteUrl('');
      setContextDocument('');
      setDocFileName(null);
      setPhase('context_panel');
    }
  };

  const handleResume = () => {
    if (!savedSession) return;

    const goal = savedSession.campaign_goal || null;
    const pc = savedSession.product_context || '';
    const icp = savedSession.icp_description || '';
    const dk = savedSession.domain_keyword || '';
    const wu = savedSession.website_url || '';
    const cd = savedSession.context_document || '';

    setCampaignGoal(goal);
    setProductContext(pc);
    setIcpDescription(icp);
    setDomainKeyword(dk);
    setWebsiteUrl(wu);
    setContextDocument(cd);
    setDocFileName(null);
    campaignGoalRef.current = goal;
    productContextRef.current = pc;
    icpDescriptionRef.current = icp;
    domainKeywordRef.current = dk;
    websiteUrlRef.current = wu;
    contextDocumentRef.current = cd;

    setDisplayHistory(savedSession.display_history || []);
    setApiHistory(savedSession.api_history || []);
    setInitialAnalysis(savedSession.initial_analysis || null);
    setTurnsUsed(savedSession.turns_used || 0);

    if (savedSession.initial_analysis) {
      setParsedCampaigns(parseAICampaigns(savedSession.initial_analysis));
    }

    setPhase('analyzing');
  };

  const handleStartFresh = async () => {
    if (campaignId && savedSession) {
      await supabase
        .from('campaign_ai_sessions')
        .delete()
        .eq('campaign_id', campaignId);
    }
    setSavedSession(null);
    setCampaignGoal(initialGoal ?? null);
    setProductContext('');
    setIcpDescription('');
    setDomainKeyword('');
    setWebsiteUrl('');
    setContextDocument('');
    setDocFileName(null);
    campaignGoalRef.current = null;
    productContextRef.current = '';
    icpDescriptionRef.current = '';
    domainKeywordRef.current = '';
    websiteUrlRef.current = '';
    contextDocumentRef.current = '';
    setPhase('context_panel');
  };

  // ── Persist session after each turn ──────────────────────────────────────
  const saveSession = useCallback(async (
    displayHist: ChatMessage[],
    apiHist: ChatMessage[],
    initAnalysis: string | null,
    turns: number,
  ) => {
    if (!campaignId) return;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    supabase
      .from('campaign_ai_sessions')
      .upsert({
        campaign_id: campaignId,
        user_id: user.id,
        display_history: displayHist,
        api_history: apiHist,
        initial_analysis: initAnalysis,
        turns_used: turns,
        product_context: productContextRef.current || null,
        icp_description: icpDescriptionRef.current || null,
        domain_keyword: domainKeywordRef.current || null,
        website_url: websiteUrlRef.current || null,
        context_document: contextDocumentRef.current || null,
        campaign_goal: campaignGoalRef.current || null,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'campaign_id,user_id' })
      .then(({ error }) => {
        if (error) console.error('Failed to save AI session:', error);
      });
  }, [campaignId]);

  // ── Scroll to bottom on new content ──────────────────────────────────────
  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [displayHistory, initialAnalysis]);

  // ── Focus sub-input when it opens ────────────────────────────────────────
  useEffect(() => {
    if (activeSubInput) {
      setTimeout(() => subInputRef.current?.focus(), 50);
    }
  }, [activeSubInput]);

  // ── Cleanup on unmount ────────────────────────────────────────────────────
  useEffect(() => {
    return () => { abortRef.current?.abort(); };
  }, []);

  // ── URL validation ────────────────────────────────────────────────────────
  const validateUrl = (url: string): boolean => {
    if (!url) return true;
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  };

  // ── Build context object for Edge Function ────────────────────────────────
  const buildFullContext = () => ({
    ...campaignContext,
    campaign_goal: campaignGoalRef.current || undefined,
    product_context: productContextRef.current || undefined,
    icp_description: icpDescriptionRef.current || undefined,
    domain_keyword: domainKeywordRef.current || undefined,
    website_url: websiteUrlRef.current || undefined,
    context_document: contextDocumentRef.current || undefined,
  });

  // ── Call Edge Function ────────────────────────────────────────────────────
  const callAnalyzer = async (
    turnNumber: number,
    history: ChatMessage[],
    userMessage?: string
  ): Promise<string> => {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const controller = new AbortController();
    abortRef.current = controller;
    const timeoutId = setTimeout(() => controller.abort(), ANALYSIS_TIMEOUT_MS);

    try {
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/campaign-ai-analyzer`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          signal: controller.signal,
          body: JSON.stringify({
            messages: messages.map((m) => ({
              message_number: m.message_number,
              content: m.content,
              character_count: m.character_count,
            })),
            campaign_context: buildFullContext(),
            conversation_history: history,
            turn_number: turnNumber,
            ...(userMessage ? { user_message: userMessage } : {}),
          }),
        }
      );

      clearTimeout(timeoutId);

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        if (body.error === 'turn_limit_reached') throw new Error('turn_limit_reached');
        throw new Error(body.error || `Request failed (${res.status})`);
      }

      const data = await res.json();
      return data.response;
    } catch (err: any) {
      clearTimeout(timeoutId);
      if (err.name === 'AbortError') throw new Error('timeout');
      throw err;
    }
  };

  // ── Start analysis from context panel ────────────────────────────────────
  const handleAnalyze = async () => {
    if (!campaignGoal) return;
    if (!validateUrl(websiteUrl)) {
      setWebsiteUrlError('Please enter a valid URL starting with https:// or http://');
      return;
    }
    setWebsiteUrlError('');

    // Commit to refs so callAnalyzer uses these values on all turns
    campaignGoalRef.current = campaignGoal;
    productContextRef.current = productContext;
    icpDescriptionRef.current = icpDescription;
    domainKeywordRef.current = domainKeyword;
    websiteUrlRef.current = websiteUrl;
    contextDocumentRef.current = contextDocument;

    // Epoch 80: write campaign_goal back to campaigns table (canonical source)
    if (campaignId && campaignGoal) {
      supabase
        .from('campaigns')
        .update({ campaign_goal: campaignGoal })
        .eq('id', campaignId)
        .then(({ error }) => {
          if (error) console.error('Failed to write campaign_goal to campaigns:', error);
        });
    }

    setPhase('analyzing');
    setIsLoading(true);
    setError(null);

    try {
      const response = await callAnalyzer(0, []);
      setInitialAnalysis(response);
      const parsed = parseAICampaigns(response);
      setParsedCampaigns(parsed);
      saveSession([], [], response, 0);
    } catch (err: any) {
      if (err.message === 'timeout') {
        setError('Analysis is taking longer than expected. Please close and try again.');
      } else {
        setError('Analysis failed — please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ── Typed follow-up send ──────────────────────────────────────────────────
  const handleSend = async () => {
    const text = inputValue.trim();
    if (!text || isLoading || turnsUsed >= MAX_FOLLOW_UPS) return;

    const msg: ChatMessage = { role: 'user', content: text };
    const updatedDisplay = [...displayHistory, msg];
    const updatedApi = [...apiHistory, msg];

    setDisplayHistory(updatedDisplay);
    setApiHistory(updatedApi);
    setInputValue('');
    setIsLoading(true);
    setError(null);

    try {
      const response = await callAnalyzer(turnsUsed + 1, updatedApi, text);
      const assistantMsg: ChatMessage = { role: 'assistant', content: response };
      const finalDisplay = [...updatedDisplay, assistantMsg];
      const finalApi = [...updatedApi, assistantMsg];
      setDisplayHistory(finalDisplay);
      setApiHistory(finalApi);
      const newTurns = turnsUsed + 1;
      setTurnsUsed(newTurns);

      // Parse any new campaigns from this response
      const newParsed = parseAICampaigns(response);
      if (newParsed.length > 0) {
        setParsedCampaigns(prev => {
          const existing = new Set(prev.map(c => c.title));
          return [...prev, ...newParsed.filter(c => !existing.has(c.title))];
        });
      }

      saveSession(finalDisplay, finalApi, initialAnalysis, newTurns);
    } catch (err: any) {
      if (err.message === 'timeout') {
        setError('Response timed out — please try again.');
      } else {
        setError('Message failed — please try again.');
      }
      setDisplayHistory(displayHistory);
      setApiHistory(apiHistory);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Guided action send ────────────────────────────────────────────────────
  const handleGuidedAction = async (action: GuidedAction, inputVal?: string) => {
    if (isLoading || turnsUsed >= MAX_FOLLOW_UPS) return;

    const fullPrompt = action.requiresInput && inputVal
      ? action.prompt.replace(action.inputTokenPlaceholder!, inputVal.trim())
      : action.prompt;

    const displayMsg: ChatMessage = { role: 'user', content: action.label };
    const apiMsg: ChatMessage = { role: 'user', content: fullPrompt };

    const updatedDisplay = [...displayHistory, displayMsg];
    const updatedApi = [...apiHistory, apiMsg];

    setDisplayHistory(updatedDisplay);
    setApiHistory(updatedApi);
    setIsLoading(true);
    setError(null);
    setActiveSubInput(null);
    setSubInputValue('');

    try {
      const response = await callAnalyzer(turnsUsed + 1, updatedApi, fullPrompt);
      const assistantMsg: ChatMessage = { role: 'assistant', content: response };
      const finalDisplay = [...updatedDisplay, assistantMsg];
      const finalApi = [...updatedApi, assistantMsg];
      setDisplayHistory(finalDisplay);
      setApiHistory(finalApi);
      const newTurns = turnsUsed + 1;
      setTurnsUsed(newTurns);

      // Parse any new campaigns from this response
      const newParsed = parseAICampaigns(response);
      if (newParsed.length > 0) {
        setParsedCampaigns(prev => {
          const existing = new Set(prev.map(c => c.title));
          return [...prev, ...newParsed.filter(c => !existing.has(c.title))];
        });
      }

      saveSession(finalDisplay, finalApi, initialAnalysis, newTurns);
    } catch (err: any) {
      if (err.message === 'timeout') {
        setError('Response timed out — please try again.');
      } else {
        setError('Message failed — please try again.');
      }
      setDisplayHistory(displayHistory);
      setApiHistory(apiHistory);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSubInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, action: GuidedAction) => {
    if (e.key === 'Enter' && subInputValue.trim()) {
      e.preventDefault();
      handleGuidedAction(action, subInputValue);
    }
    if (e.key === 'Escape') {
      setActiveSubInput(null);
      setSubInputValue('');
    }
  };

  const turnsRemaining = MAX_FOLLOW_UPS - turnsUsed;
  const inputDisabled = isLoading || turnsUsed >= MAX_FOLLOW_UPS || !initialAnalysis;
  const actionsDisabled = isLoading || turnsUsed >= MAX_FOLLOW_UPS;

  const contextLine = [
    campaignContext.target_role,
    campaignContext.geography,
    campaignContext.industry,
  ].filter(Boolean).join('  ·  ').slice(0, 60);

  if (!isOpen) return null;

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-lg">✨</span>
            <h2 className="text-lg font-semibold text-gray-900">AI Campaign Analyzer</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ── Message pills + ICP context indicator ────────────────────── */}
        <div className="px-6 py-3 border-b border-gray-100 bg-gray-50 flex-shrink-0">
          <p className="text-xs font-medium text-gray-500 mb-2">
            Analyzing {messages.length} message{messages.length === 1 ? '' : 's'}
          </p>
          <div className="flex flex-wrap gap-2">
            {messages.map((m) => (
              <span
                key={m.message_number}
                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700"
              >
                Message {m.message_number}
              </span>
            ))}
          </div>
          {contextLine && (
            <p className="text-xs text-gray-400 mt-1.5">Targeting: {contextLine}</p>
          )}
        </div>

        {/* ── Phase: loading_session ──────────────────────────────────────── */}
        {phase === 'loading_session' && (
          <div className="flex-1 flex items-center justify-center py-12">
            <div className="flex gap-1">
              <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}

        {/* ── Phase: resume_prompt ─────────────────────────────────────────── */}
        {phase === 'resume_prompt' && savedSession && (
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="w-full max-w-sm bg-gray-50 border border-gray-200 rounded-xl p-6 text-center">
              <div className="text-2xl mb-3">💬</div>
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Previous analysis found</h3>
              <p className="text-xs text-gray-500 mb-5">
                {new Date(savedSession.created_at).toLocaleDateString('en-US', {
                  month: 'short', day: 'numeric', year: 'numeric'
                })} · {savedSession.turns_used} turn{savedSession.turns_used === 1 ? '' : 's'} used
              </p>
              <div className="flex flex-col gap-2">
                <button
                  onClick={handleResume}
                  className="w-full px-4 py-2.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Resume previous session
                </button>
                <button
                  onClick={handleStartFresh}
                  className="w-full px-4 py-2.5 bg-white text-gray-600 text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  Start fresh
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Phase: context_panel ─────────────────────────────────────────── */}
        {phase === 'context_panel' && (
          <div className="flex-1 overflow-y-auto px-6 py-5">

            {/* Campaign goal — required */}
            <div className="mb-6">
              <p className="text-sm font-medium text-gray-800 mb-3">What is the goal of this campaign?</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setCampaignGoal('product_discovery')}
                  className={`rounded-xl border-2 px-4 py-4 text-left transition-all ${
                    campaignGoal === 'product_discovery'
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="text-lg mb-1">🔬</div>
                  <div className="text-sm font-semibold text-gray-900">Product Discovery</div>
                  <div className="text-xs text-gray-500 mt-0.5">Validate a market hypothesis</div>
                </button>
                <button
                  onClick={() => setCampaignGoal('lead_generation')}
                  className={`rounded-xl border-2 px-4 py-4 text-left transition-all ${
                    campaignGoal === 'lead_generation'
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="text-lg mb-1">🎯</div>
                  <div className="text-sm font-semibold text-gray-900">Lead Generation</div>
                  <div className="text-xs text-gray-500 mt-0.5">Book meetings / convert leads</div>
                </button>
              </div>
            </div>

            <p className="text-xs text-gray-400 mb-5">
              Optional context below — leave blank to analyze message structure only.
            </p>

            <div className="space-y-4">
              {/* Product context */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  What does your product or service do?
                </label>
                <textarea
                  value={productContext}
                  onChange={(e) => setProductContext(e.target.value)}
                  placeholder="e.g. We help creative agencies prove the ROI of their AI-generated video work to clients, using automated documentation and audit trails."
                  rows={3}
                  className="w-full resize-none border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Target audience / ICP */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Target audience / ICP
                  <span className="text-gray-400 font-normal ml-1">(describe your ideal customer)</span>
                </label>
                <textarea
                  value={icpDescription}
                  onChange={(e) => setIcpDescription(e.target.value)}
                  placeholder={campaignContext.target_role
                    ? `e.g. All are at Series A–B companies, primarily ex-pharma background, use LinkedIn daily`
                    : `e.g. VP of Sales at Series A–B biotech companies in NYC, 50–200 employees`}
                  rows={2}
                  className="w-full resize-none border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                {!campaignContext.target_role && (
                  <p className="text-xs text-gray-400 mt-1">
                    No target list found — describe your ICP here so the AI can check message-audience fit.
                  </p>
                )}
              </div>

              {/* Buyer's niche or vertical */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Buyer's niche or vertical
                  <span className="text-gray-400 font-normal ml-1">(the term your buyers use to describe their own world)</span>
                </label>
                <input
                  type="text"
                  value={domainKeyword}
                  onChange={(e) => setDomainKeyword(e.target.value)}
                  placeholder="e.g. AI video, WhatsApp automation, immigration law, gaming"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Website URL */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Company website
                  <span className="text-gray-400 font-normal ml-1">(we'll read your homepage for value prop context)</span>
                </label>
                <input
                  type="url"
                  value={websiteUrl}
                  onChange={(e) => {
                    setWebsiteUrl(e.target.value);
                    if (websiteUrlError) setWebsiteUrlError('');
                  }}
                  placeholder="https://yourcompany.com"
                  className={`w-full border rounded-lg px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                    websiteUrlError ? 'border-red-400' : 'border-gray-300'
                  }`}
                />
                {websiteUrlError && (
                  <p className="text-xs text-red-500 mt-1">{websiteUrlError}</p>
                )}
                <p className="text-xs text-gray-400 mt-1">
                  🔒 Fetched securely server-side. Only your homepage is read.
                </p>
              </div>

              {/* Context document */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Context document
                  <span className="text-gray-400 font-normal ml-1">(pitch deck, positioning doc, case studies)</span>
                </label>
                <div className="flex items-center gap-3">
                  <label className={`cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                    docLoading
                      ? 'border-gray-200 bg-gray-50 text-gray-400 pointer-events-none'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                  }`}>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    {docLoading ? 'Reading…' : 'Upload PDF or .txt'}
                    <input
                      type="file"
                      accept=".pdf,.txt,.md"
                      className="hidden"
                      disabled={docLoading}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        e.target.value = '';
                        setDocError(null);
                        setDocLoading(true);
                        try {
                          let text: string;
                          if (file.name.toLowerCase().endsWith('.pdf')) {
                            text = await extractPdfText(file);
                          } else {
                            text = await file.text();
                          }
                          setContextDocument(text.slice(0, 8000));
                          setDocFileName(file.name);
                        } catch {
                          setDocError('Could not read file — try a different format.');
                        } finally {
                          setDocLoading(false);
                        }
                      }}
                    />
                  </label>
                  {docFileName && !docLoading && (
                    <span className="text-xs text-gray-600 flex items-center gap-1">
                      📄 {docFileName}
                      <button
                        onClick={() => { setContextDocument(''); setDocFileName(null); setDocError(null); }}
                        className="ml-0.5 text-gray-400 hover:text-gray-600"
                        aria-label="Remove file"
                      >✕</button>
                    </span>
                  )}
                </div>
                {docError && <p className="text-xs text-red-500 mt-1">{docError}</p>}
              </div>
            </div>

            <button
              onClick={handleAnalyze}
              disabled={!campaignGoal}
              className="mt-6 w-full px-4 py-3 bg-purple-600 text-white text-sm font-semibold rounded-lg hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              <span>✨</span>
              <span>Analyze with AI</span>
            </button>
          </div>
        )}

        {/* ── Phase: analyzing ──────────────────────────────────────────────── */}
        {phase === 'analyzing' && (
          <>
            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">

              {/* Loading — initial */}
              {isLoading && !initialAnalysis && (
                <div className="flex items-center gap-3 text-gray-500 py-8 justify-center">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                  <span className="text-sm">
                    Analyzing{websiteUrlRef.current ? ' and reading your website' : ''}
                    {campaignGoalRef.current === 'product_discovery' ? ' (discovery mode)' : ''}...
                  </span>
                </div>
              )}

              {/* Error — initial */}
              {error && !isLoading && !initialAnalysis && (
                <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-4">
                  <span className="text-red-500 mt-0.5">⚠</span>
                  <div className="flex-1">
                    <p className="text-sm text-red-700">{error}</p>
                    <button
                      onClick={handleAnalyze}
                      className="mt-2 text-sm font-medium text-red-600 hover:text-red-800 underline"
                    >
                      Retry analysis
                    </button>
                  </div>
                </div>
              )}

              {/* Initial analysis */}
              {initialAnalysis && (
                <div className="space-y-0.5">
                  {renderMarkdown(initialAnalysis)}
                </div>
              )}

              {/* Load into editor — all parsed campaigns */}
              {parsedCampaigns.length > 0 && onLoadMessages && (
                <div className="mt-2 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <p className="text-xs font-medium text-indigo-700 mb-2">↓ Load into editor</p>
                  <div className="space-y-1.5">
                    {parsedCampaigns.map((campaign, i) => (
                      <button
                        key={i}
                        onClick={() => onLoadMessages(toMessageContents(campaign))}
                        className="w-full text-left px-3 py-2 text-xs bg-white border border-indigo-200 rounded-lg hover:bg-indigo-50 hover:border-indigo-300 transition-colors flex items-center gap-2"
                      >
                        <span className="text-indigo-400">↓</span>
                        <span className="font-medium text-indigo-700">{campaign.title}</span>
                        <span className="text-indigo-400 ml-auto">({campaign.messages.length} msgs)</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Follow-up conversation */}
              {displayHistory.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                      msg.role === 'user'
                        ? 'bg-purple-600 text-white rounded-br-sm'
                        : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                    }`}
                  >
                    {msg.role === 'assistant'
                      ? <div className="space-y-0.5">{renderMarkdown(msg.content)}</div>
                      : msg.content}
                  </div>
                </div>
              ))}

              {/* Loading bubble — follow-up */}
              {isLoading && initialAnalysis && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 px-4 py-3 rounded-2xl rounded-bl-sm">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}

              <div ref={chatBottomRef} />
            </div>

            {/* ── Bottom panel ──────────────────────────────────────────── */}
            {initialAnalysis && (
              <div className="border-t border-gray-200 flex-shrink-0">
                {turnsUsed >= MAX_FOLLOW_UPS ? (
                  <div className="px-6 py-4">
                    <p className="text-sm text-gray-500 text-center py-2">
                      You've reached the limit for this session. Close and re-open to start a new analysis.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Guided action buttons */}
                    <div className="px-6 pt-4 pb-3">
                      <p className="text-xs font-medium text-gray-400 mb-2">Quick actions</p>
                      <div className="flex flex-wrap gap-2">
                        {guidedActions.map((action) => (
                          <button
                            key={action.label}
                            onClick={() => {
                              if (actionsDisabled) return;
                              if (action.requiresInput) {
                                setActiveSubInput(
                                  activeSubInput === action.label ? null : action.label
                                );
                                setSubInputValue('');
                              } else {
                                handleGuidedAction(action);
                              }
                            }}
                            disabled={actionsDisabled}
                            className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                              activeSubInput === action.label
                                ? 'bg-purple-100 border-purple-400 text-purple-800'
                                : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 hover:border-gray-300'
                            } disabled:opacity-40 disabled:cursor-not-allowed`}
                          >
                            {action.label}
                          </button>
                        ))}
                      </div>

                      {/* Sub-input for requiresInput actions */}
                      {activeSubInput && (() => {
                        const action = guidedActions.find(a => a.label === activeSubInput);
                        if (!action) return null;
                        return (
                          <div className="mt-2 flex gap-2 items-center">
                            <input
                              ref={subInputRef}
                              type="text"
                              value={subInputValue}
                              onChange={(e) => setSubInputValue(e.target.value)}
                              onKeyDown={(e) => handleSubInputKeyDown(e, action)}
                              placeholder={action.inputPlaceholder}
                              className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            />
                            <button
                              onClick={() => {
                                if (subInputValue.trim()) handleGuidedAction(action, subInputValue);
                              }}
                              disabled={!subInputValue.trim()}
                              className="px-3 py-1.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                              →
                            </button>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Text input */}
                    <div className="px-6 pb-4 border-t border-gray-100 pt-3">
                      <div className="flex gap-3 items-end">
                        <textarea
                          value={inputValue}
                          onChange={(e) => setInputValue(e.target.value)}
                          onKeyDown={handleKeyDown}
                          disabled={inputDisabled}
                          placeholder="Ask a follow-up question... (Enter to send, Shift+Enter for new line)"
                          rows={2}
                          className="flex-1 resize-none border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                        />
                        <button
                          onClick={handleSend}
                          disabled={inputDisabled || !inputValue.trim()}
                          className="flex-shrink-0 px-4 py-2.5 bg-purple-600 text-white text-sm font-medium rounded-xl hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          Send
                        </button>
                      </div>
                      <p className="text-xs text-gray-400 mt-2 text-right">
                        {turnsRemaining} follow-up question{turnsRemaining === 1 ? '' : 's'} remaining
                      </p>
                      {error && !isLoading && initialAnalysis && (
                        <p className="text-xs text-red-500 mt-1">{error}</p>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
};

export default CampaignAnalyzerModal;
